import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { getProfileText, getRoleText } from '../utils/helpers';
import { useModuleSettings } from '../context/ModuleSettingsContext';
import logoIcon from '../assets/logo-icon.png';
import { t } from '../i18n';

const Sidebar = ({
  currentUser = { name: 'Usuario', role: 'comprador', avatar: 'U' },
  currentPage = 'dashboard',
  onNavigate = () => {},
  allowedPages = null,
  collapsed = false,
  onToggleCollapsed = () => {}
}) => {
  const role = currentUser.role || 'comprador';
  const { parks, preferences, updatePreferences } = useAppContext();
  const { navItems } = useModuleSettings();
  const selectedParkId = preferences?.projectContext?.parkId || 'all';

  useEffect(() => {
    if (!Array.isArray(parks) || parks.length === 0) return;
    if (selectedParkId !== 'all' && parks.some((park) => park.id === selectedParkId)) return;
    updatePreferences({ projectContext: { parkId: parks[0]?.id || 'all' } });
  }, [parks, selectedParkId, updatePreferences]);

  const canAccess = (itemId) => {
    if (!allowedPages) return true;
    return allowedPages.has(itemId);
  };
  const hasCustomAccess = Boolean(allowedPages);

  const visibleItems = navItems.map((item) => {
    const roleCheck = hasCustomAccess ? true : !item.roles || item.roles.includes(role);
    if (!roleCheck) return null;

    if (item.children) {
      const filteredChildren = item.children.filter((child) => {
        const childRoleCheck = hasCustomAccess ? true : !child.roles || child.roles.includes(role);
        const childPageCheck = canAccess(child.id);
        return childRoleCheck && childPageCheck;
      });
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    }

    const pageCheck = canAccess(item.id);
    return pageCheck ? item : null;
  }).filter(Boolean);

  const defaultOpenModules = useMemo(() => {
    const initial = {};
    visibleItems.forEach((item) => {
      if (item.children) initial[item.id] = true;
    });
    return initial;
  }, [currentUser.role]);

  const [openModules, setOpenModules] = useState(defaultOpenModules);

  const toggleModule = (moduleId) => {
    setOpenModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const width = collapsed ? 'w-14' : 'w-60';

  return (
    <aside className={`fixed left-0 top-0 h-full ${width} bg-neutral-900 text-neutral-200 flex flex-col z-40 transition-[width] duration-150`}>
      <div className={`flex items-center gap-2.5 h-12 border-b border-neutral-800 ${collapsed ? 'justify-center px-0' : 'px-4'}`}>
        <img src={logoIcon} alt={t('KOTA+')} className="w-6 h-6 shrink-0" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white leading-none truncate">{t('KOTA+')}</p>
            <p className="text-neutral-500 text-[10px] font-medium uppercase tracking-wider leading-none mt-1">{t('Energy System')}</p>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="px-3 py-2.5 border-b border-neutral-800">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-200 shrink-0">
              {t(currentUser.avatar)}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-white truncate">{t(currentUser.name)}</p>
              <p className="text-[11px] text-neutral-500 truncate">
                {currentUser.profile ? t(getProfileText(currentUser.profile)) : t(getRoleText(currentUser.role))}
              </p>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          if (item.children) {
            const isGroupActive =
              item.id === 'projects-module'
                ? currentPage === 'projects' || currentPage.startsWith('projects-')
                : item.children.some((child) => child.id === currentPage);
            const isOpen = collapsed ? false : (openModules[item.id] ?? true);

            return (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={() => (item.pageId ? onNavigate(item.pageId) : toggleModule(item.id))}
                  className={`w-full flex items-center gap-2.5 h-8 text-xs font-medium ${collapsed ? 'justify-center px-0' : 'px-3'} ${
                    isGroupActive ? 'text-white bg-neutral-800' : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100'
                  }`}
                  title={collapsed ? t(item.label) : undefined}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left truncate">{t(item.label)}</span>
                      <span
                        role="presentation"
                        onClick={(event) => { event.stopPropagation(); toggleModule(item.id); }}
                      >
                        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </span>
                    </>
                  )}
                </button>
                {isOpen && !collapsed && (
                  <div>
                    {item.children.map((child) => {
                      const isActiveChild = currentPage === child.id;
                      return (
                        <button
                          key={child.id}
                          onClick={() => onNavigate(child.id)}
                          className={`w-full flex items-center gap-2.5 h-7 pl-9 pr-3 text-xs ${
                            isActiveChild
                              ? 'bg-brand-600/15 text-brand-300 border-r-2 border-brand-500'
                              : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100'
                          }`}
                        >
                          <span className="truncate text-left">{t(child.label)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const isActive = currentPage === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              title={collapsed ? t(item.label) : undefined}
              className={`w-full flex items-center gap-2.5 h-8 text-xs font-medium ${collapsed ? 'justify-center px-0' : 'px-3'} ${
                isActive ? 'text-white bg-neutral-800' : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100'
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              {!collapsed && <span className="truncate">{t(item.label)}</span>}
            </button>
          );
        })}
      </nav>

      <button
        type="button"
        onClick={onToggleCollapsed}
        className="h-9 border-t border-neutral-800 flex items-center justify-center text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800"
        aria-label={collapsed ? t('Expandir menú') : t('Colapsar menú')}
      >
        {collapsed ? <ChevronsRight className="w-3.5 h-3.5" /> : <ChevronsLeft className="w-3.5 h-3.5" />}
      </button>
    </aside>
  );
};

export default Sidebar;
